import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Image from '../../../components/AppImage';

const ReviewsSection = ({ vendor }) => {
  const [showAllReviews, setShowAllReviews] = useState(false);
  const [filterByCulture, setFilterByCulture] = useState('all');
  const [filterByEventType, setFilterByEventType] = useState('all');

  const renderStars = (rating) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 !== 0;
    
    for (let i = 0; i < fullStars; i++) {
      stars.push(
        <Icon key={i} name="Star" size={14} color="#DAA520" strokeWidth={0} className="fill-current" />
      );
    }
    
    if (hasHalfStar) {
      stars.push(
        <Icon key="half" name="Star" size={14} color="#DAA520" strokeWidth={1} className="fill-current opacity-50" />
      );
    }
    
    const remainingStars = 5 - Math.ceil(rating);
    for (let i = 0; i < remainingStars; i++) {
      stars.push(
        <Icon key={`empty-${i}`} name="Star" size={14} color="#E8E2DD" strokeWidth={1} />
      );
    }
    
    return stars;
  };

  const getFilteredReviews = () => {
    let filtered = vendor.reviews;
    
    if (filterByCulture !== 'all') {
      filtered = filtered.filter(review => review.culture === filterByCulture);
    }
    
    if (filterByEventType !== 'all') {
      filtered = filtered.filter(review => review.eventType === filterByEventType);
    }
    
    return showAllReviews ? filtered : filtered.slice(0, 3);
  };

  const cultures = [...new Set(vendor.reviews.map(review => review.culture))];
  const eventTypes = [...new Set(vendor.reviews.map(review => review.eventType))];
  const filteredReviews = getFilteredReviews();

  const ratingDistribution = {
    5: vendor.reviews.filter(r => r.rating === 5).length,
    4: vendor.reviews.filter(r => r.rating === 4).length,
    3: vendor.reviews.filter(r => r.rating === 3).length,
    2: vendor.reviews.filter(r => r.rating === 2).length,
    1: vendor.reviews.filter(r => r.rating === 1).length,
  };

  return (
    <div className="bg-white rounded-xl border border-border shadow-soft">
      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="font-heading font-semibold text-xl text-text-primary">
            Reviews & Testimonials
          </h3>
          <div className="flex items-center space-x-2">
            <div className="flex items-center space-x-1">
              {renderStars(vendor.rating)}
              <span className="ml-2 font-heading font-bold text-lg text-text-primary">
                {vendor.rating}
              </span>
            </div>
            <span className="text-text-secondary font-caption">
              ({vendor.reviewCount} reviews)
            </span>
          </div>
        </div>

        {/* Rating Distribution */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          <div>
            <h4 className="font-medium text-text-primary mb-3">Rating Breakdown</h4>
            <div className="space-y-2">
              {[5, 4, 3, 2, 1].map((rating) => (
                <div key={rating} className="flex items-center space-x-3">
                  <span className="text-sm text-text-secondary w-8">{rating} star</span>
                  <div className="flex-1 h-2 bg-surface-200 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-secondary transition-all duration-500"
                      style={{ 
                        width: `${vendor.reviewCount > 0 ? (ratingDistribution[rating] / vendor.reviewCount) * 100 : 0}%` 
                      }}
                    />
                  </div>
                  <span className="text-sm text-text-secondary w-8 text-right">
                    {ratingDistribution[rating]}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div>
            <h4 className="font-medium text-text-primary mb-3">Review Highlights</h4>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm text-text-secondary">Cultural Authenticity</span>
                <div className="flex items-center space-x-1">
                  {renderStars(4.8)}
                  <span className="text-sm font-medium text-text-primary ml-1">4.8</span>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-text-secondary">Professionalism</span>
                <div className="flex items-center space-x-1">
                  {renderStars(4.9)}
                  <span className="text-sm font-medium text-text-primary ml-1">4.9</span>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-text-secondary">Value for Money</span>
                <div className="flex items-center space-x-1">
                  {renderStars(4.7)}
                  <span className="text-sm font-medium text-text-primary ml-1">4.7</span>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-text-secondary">Communication</span>
                <div className="flex items-center space-x-1">
                  {renderStars(4.8)}
                  <span className="text-sm font-medium text-text-primary ml-1">4.8</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Review Filters */}
        <div className="flex flex-wrap items-center gap-3 mb-6">
          <div className="flex items-center space-x-2">
            <span className="text-sm font-medium text-text-primary">Filter by:</span>
            <select
              value={filterByCulture}
              onChange={(e) => setFilterByCulture(e.target.value)}
              className="px-3 py-1 border border-border rounded-lg text-sm focus:ring-2 focus:ring-primary focus:border-primary"
            >
              <option value="all">All Cultures</option>
              {cultures.map((culture) => (
                <option key={culture} value={culture}>{culture}</option>
              ))}
            </select>
            <select
              value={filterByEventType}
              onChange={(e) => setFilterByEventType(e.target.value)}
              className="px-3 py-1 border border-border rounded-lg text-sm focus:ring-2 focus:ring-primary focus:border-primary"
            >
              <option value="all">All Events</option>
              {eventTypes.map((eventType) => (
                <option key={eventType} value={eventType}>{eventType}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Reviews List */}
        <div className="space-y-6">
          {filteredReviews.map((review) => (
            <div key={review.id} className="border-b border-border-light last:border-b-0 pb-6 last:pb-0">
              <div className="flex items-start space-x-4">
                <Image
                  src={review.clientPhoto}
                  alt={review.clientName}
                  className="w-12 h-12 rounded-full object-cover"
                />
                
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <div className="font-medium text-text-primary">{review.clientName}</div>
                      <div className="flex items-center space-x-2 text-sm text-text-secondary">
                        <span className="font-caption">{review.culture} • {review.eventType}</span>
                        <div className="w-1 h-1 bg-text-muted rounded-full" />
                        <span className="font-caption">{review.date}</span>
                      </div>
                    </div>
                    <div className="flex items-center space-x-1">
                      {renderStars(review.rating)}
                      <span className="ml-1 text-sm font-medium text-text-primary">
                        {review.rating}
                      </span>
                    </div>
                  </div>
                  
                  <p className="text-text-secondary font-caption leading-relaxed mb-3">
                    {review.comment}
                  </p>
                  
                  {review.photos && review.photos.length > 0 && (
                    <div className="flex space-x-2 mb-3">
                      {review.photos.slice(0, 3).map((photo, index) => (
                        <Image
                          key={index}
                          src={photo}
                          alt={`Review photo ${index + 1}`}
                          className="w-16 h-16 rounded-lg object-cover"
                        />
                      ))}
                      {review.photos.length > 3 && (
                        <div className="w-16 h-16 rounded-lg bg-surface-100 flex items-center justify-center">
                          <span className="text-xs text-text-secondary font-data">
                            +{review.photos.length - 3}
                          </span>
                        </div>
                      )}
                    </div>
                  )}
                  
                  <div className="flex items-center space-x-4 text-sm">
                    <button className="flex items-center space-x-1 text-text-secondary hover:text-primary transition-smooth">
                      <Icon name="ThumbsUp" size={14} color="currentColor" />
                      <span className="font-caption">Helpful ({review.helpfulCount})</span>
                    </button>
                    
                    {review.vendorResponse && (
                      <button className="flex items-center space-x-1 text-text-secondary hover:text-primary transition-smooth">
                        <Icon name="MessageCircle" size={14} color="currentColor" />
                        <span className="font-caption">Vendor Response</span>
                      </button>
                    )}
                  </div>
                  
                  {review.vendorResponse && (
                    <div className="mt-3 p-3 bg-surface-50 rounded-lg">
                      <div className="flex items-center space-x-2 mb-2">
                        <Icon name="Store" size={14} color="#8B4513" />
                        <span className="text-sm font-medium text-primary">
                          Response from {vendor.name}
                        </span>
                        <span className="text-xs text-text-muted font-caption">
                          {review.vendorResponse.date}
                        </span>
                      </div>
                      <p className="text-sm text-text-secondary font-caption">
                        {review.vendorResponse.message}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Show More Button */}
        {vendor.reviews.length > 3 && (
          <div className="mt-6 text-center">
            <Button
              variant="outline"
              size="md"
              onClick={() => setShowAllReviews(!showAllReviews)}
              iconName={showAllReviews ? "ChevronUp" : "ChevronDown"}
              iconSize={16}
            >
              {showAllReviews ? 'Show Less Reviews' : `Show All ${vendor.reviews.length} Reviews`}
            </Button>
          </div>
        )}

        {/* Write Review CTA */}
        <div className="mt-6 p-4 bg-primary-50 rounded-lg">
          <div className="flex items-center justify-between">
            <div>
              <div className="font-medium text-primary">Share Your Experience</div>
              <div className="text-sm text-text-secondary font-caption">
                Help other couples by writing a review
              </div>
            </div>
            <Button
              variant="primary"
              size="sm"
              iconName="Edit"
              iconSize={16}
            >
              Write Review
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReviewsSection;